import React from 'react';

import { lumappsAnalyticsPlugin } from '../tracking';
import { LumappsTrackEventPayload, TrackingSurface, TargetType, TrackingEventType } from '../types';

/**
 * This hook is tracking clicks.
 * When the tracked element (ref attached) is clicked, or the returned onClick handler is invoked,
 * it will trigger a batched tracking event
 * @param targetId string
 * @param targetType TargetType
 * @param triggerOnce boolean - if true, the click will be tracked only once (defaults to false)
 * @param tracking optional tracking metadata: the surface the item is rendered in, and
 * `isNavigation` for clicks that take the user off the page (see below)
 * @returns ref to attach to the tracked element, and an onClick handler for elements that
 * cannot take a ref (plain function components)
 */
export const useClickTracking = (
    targetId: string,
    targetType: TargetType,
    triggerOnce?: boolean,
    tracking?: { surface?: TrackingSurface; isNavigation?: boolean },
) => {
    const hasBeenClicked = React.useRef(false);
    const ref = React.useRef<HTMLElement>(null);
    const { surface, isNavigation } = tracking ?? {};

    const handleClick = React.useCallback(() => {
        if (triggerOnce && hasBeenClicked.current) {
            return;
        }

        const payload: LumappsTrackEventPayload = {
            action: { targetId, targetType, surface, type: TrackingEventType.CLICK_ACTION },
        };

        if (isNavigation) {
            /*
             * A click-through unloads the document well before the 3s batch debounce fires, so the
             * queued XHR never leaves the browser and the event is lost. Send it straight away with
             * the keepalive flag, which survives the unload.
             */
            lumappsAnalyticsPlugin.trackOnPageLeave({ payload });
        } else {
            lumappsAnalyticsPlugin.trackAndBatch({ payload });
        }

        if (triggerOnce) {
            hasBeenClicked.current = true;
        }
    }, [targetId, targetType, surface, isNavigation, triggerOnce]);

    // Listen only on the ref'd element. The shared useEventListener helper falls back to a
    // window-wide listener when the ref is unattached, which would emit a click event for every
    // onClick-only consumer on any click anywhere on the page.
    // The ref must be attached on first commit: a ref attached on a later render is not picked up
    // (same limitation as useEventListener) — use the returned onClick for late-mounted elements.
    React.useEffect(() => {
        const element = ref.current;
        if (!element) {
            return undefined;
        }
        element.addEventListener('click', handleClick);
        return () => element.removeEventListener('click', handleClick);
    }, [handleClick]);

    return { ref, onClick: handleClick };
};
